import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Users, UserCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";

export default function GlobalSearch() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ cards: [], boards: [] });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const SCOPES = [
    { k: "all", l: t("search.all") },
    { k: "shared", l: t("search.sharedWithMe"), icon: UserCheck },
    { k: "collaborative", l: t("search.collaborative"), icon: Users },
  ];

  useEffect(() => {
    if (!q.trim()) {
      setResults({ cards: [], boards: [] });
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q, scope } });
        setResults(data);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [q, scope]);

  return (
    <Popover open={open && q.trim().length > 0} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="global-search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={t("search.searchPh")}
            className="pl-9 h-10 bg-white/60 dark:bg-slate-900/40 border-white/60 dark:border-white/10 rounded-xl"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(420px,90vw)] p-2 glass-panel rounded-2xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center gap-1 px-1 pb-2 border-b border-white/30 dark:border-white/10">
          {SCOPES.map((s) => (
            <button
              key={s.k}
              data-testid={`search-scope-${s.k}`}
              onClick={() => setScope(s.k)}
              className={cn(
                "text-[11px] px-2 py-1 rounded-full inline-flex items-center gap-1 transition",
                scope === s.k
                  ? "bg-blue-600 text-white"
                  : "text-muted-foreground hover:bg-white/50 dark:hover:bg-white/10"
              )}
            >
              {s.icon ? <s.icon className="h-3 w-3" /> : null}
              {s.l}
            </button>
          ))}
        </div>
        {loading && (
          <div className="p-4 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t("search.searching")}</div>
        )}
        {!loading && results.boards.length === 0 && results.cards.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">{t("search.noResults")}</div>
        )}
        {results.boards.length > 0 && (
          <div className="p-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-2 py-1">{t("search.boards")}</div>
            {results.boards.map((b) => (
              <button
                key={b.board_id}
                data-testid={`search-board-${b.board_id}`}
                onClick={() => { navigate(`/boards/${b.board_id}`); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/50 dark:hover:bg-white/5 flex items-center justify-between gap-2"
              >
                <span className="font-medium truncate">{b.name}</span>
                {b.relation === "shared_with_me" && (
                  <span className="text-[9px] uppercase tracking-wider font-semibold bg-blue-500/15 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded-full">{t("search.sharedTag")}</span>
                )}
                {b.relation === "collaborative" && (
                  <span className="text-[9px] uppercase tracking-wider font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 px-1.5 py-0.5 rounded-full">{t("search.collabTag")}</span>
                )}
              </button>
            ))}
          </div>
        )}
        {results.cards.length > 0 && (
          <div className="p-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-2 py-1">{t("search.cards")}</div>
            {results.cards.map((c) => (
              <button
                key={c.card_id}
                data-testid={`search-card-${c.card_id}`}
                onClick={() => { navigate(`/boards/${c.board_id}?card=${c.card_id}`); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/50 dark:hover:bg-white/5"
              >
                <div className="font-medium truncate">{c.title}</div>
                {c.description && <div className="text-xs text-muted-foreground truncate">{c.description}</div>}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
